import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { cva } from '@styled-system/css';

import type { AnalysisPlatform } from '@/shared/api/types/generated';

import { PlatformLogo } from './PlatformLogo';

const badge = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    borderRadius: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.default',
    color: 'text.secondary',
    fontWeight: 'medium',
    whiteSpace: 'nowrap',
  },
  variants: {
    size: {
      sm: { fontSize: 'micro', gap: '1.5', paddingLeft: '1.5', paddingRight: '3', paddingY: '1' },
      md: { fontSize: 'caption', gap: '2', paddingLeft: '2', paddingRight: '2.5', paddingY: '0.5' },
    },
  },
  defaultVariants: { size: 'sm' },
});

interface PlatformBadgeProps {
  platform: AnalysisPlatform;
  size?: 'sm' | 'md';
}

// Logo un peu plus petit que la hauteur du texte + marges : la pastille reste à hauteur des étiquettes voisines.
const LOGO_SIZE = { sm: 14, md: 18 } as const;

/**
 * Badge de plateforme d'une analyse : logo + nom, sur les DEUX plateformes (décision du
 * propriétaire du 2026-10-01, qui remplace D4-2 « Vinted implicite ») — le logo rend la
 * plateforme reconnaissable d'un coup d'œil dans une liste où les deux cohabitent.
 */
export const PlatformBadge: FC<PlatformBadgeProps> = ({ platform, size = 'sm' }) => {
  const { t } = useTranslation();
  return (
    <span className={badge({ size })}>
      <PlatformLogo platform={platform} size={LOGO_SIZE[size]} />
      {t(`platform.${platform}`)}
    </span>
  );
};
